import { c as _c } from "react/compiler-runtime";
import type { SelectRootProps } from '@base-ui/react/select';
import { Select as SelectPrimitive } from '@base-ui/react/select';
import { CaretSortIcon, CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import * as React from 'react';
import { cn } from '@/lib/utils';
type PositionerProps = Pick<React.ComponentProps<typeof SelectPrimitive.Positioner>, 'align' | 'alignItemWithTrigger' | 'alignOffset' | 'anchor' | 'collisionBoundary' | 'collisionPadding' | 'side' | 'sideOffset' | 'sticky'>;
type PortalProps = Pick<React.ComponentProps<typeof SelectPrimitive.Portal>, 'container'>;
type SelectValueType<Value, Multiple extends boolean | undefined> = Multiple extends true ? Value[] : Value;
type SelectProps<Value = string, Multiple extends boolean | undefined = false> = Omit<SelectRootProps<Value, Multiple>, 'onValueChange'> & {
  onValueChange?: ((value: SelectValueType<Value, Multiple>, eventDetails: Parameters<NonNullable<SelectRootProps<Value, Multiple>['onValueChange']>>[1]) => void) | undefined;
};
function Select(t0) {
  const $ = _c(13);
  let children;
  let onValueChange;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      onValueChange,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = onValueChange;
    $[3] = props;
  } else {
    children = $[1];
    onValueChange = $[2];
    props = $[3];
  }
  if (onValueChange) {
    let t1;
    if ($[4] !== onValueChange) {
      t1 = (value, eventDetails) => {
        if (value !== null) {
          onValueChange(value as SelectValueType<Value, Multiple>, eventDetails);
        }
      };
      $[4] = onValueChange;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children || $[7] !== props || $[8] !== t1) {
      t2 = <SelectPrimitive.Root data-slot="select" onValueChange={t1} {...props}>{children}</SelectPrimitive.Root>;
      $[6] = children;
      $[7] = props;
      $[8] = t1;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    return t2;
  }
  let t1;
  if ($[10] !== children || $[11] !== props) {
    t1 = <SelectPrimitive.Root data-slot="select" {...props}>{children}</SelectPrimitive.Root>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
  } else {
    t1 = $[12];
  }
  return t1;
}
function SelectGroup(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <SelectPrimitive.Group data-slot="select-group" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SelectValue(t0) {
  const $ = _c(4);
  let props;
  if ($[0] !== t0) {
    ({
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
  } else {
    props = $[1];
  }
  let t1;
  if ($[2] !== props) {
    t1 = <SelectPrimitive.Value data-slot="select-value" {...props} />;
    $[2] = props;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
function SelectTrigger(t0) {
  const $ = _c(13);
  let children;
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = t1;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    t1 = $[4];
  }
  const size = t1 === undefined ? "default" : t1;
  let t2;
  if ($[5] !== className) {
    t2 = cn("flex w-fit items-center justify-between gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs transition-[color,box-shadow,border-color] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 data-placeholder:text-muted-foreground data-[size=default]:h-9 data-[size=sm]:h-8 *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 dark:bg-input/30 dark:hover:bg-input/50 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground", "[--sq-r:.75rem] sq:rounded-(--sq-r) sq:shadow-none", className);
    $[5] = className;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SelectPrimitive.Icon><CaretSortIcon className="size-4 opacity-50" /></SelectPrimitive.Icon>;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children || $[9] !== props || $[10] !== size || $[11] !== t2) {
    t4 = <SelectPrimitive.Trigger data-slot="select-trigger" data-size={size} className={t2} {...props}>{children}{t3}</SelectPrimitive.Trigger>;
    $[8] = children;
    $[9] = props;
    $[10] = size;
    $[11] = t2;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function SelectContent(t0) {
  const $ = _c(46);
  let align;
  let alignItemWithTrigger;
  let alignOffset;
  let anchor;
  let children;
  let className;
  let collisionBoundary;
  let collisionPadding;
  let container;
  let props;
  let side;
  let sideOffset;
  let sticky;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      children,
      position: t1,
      align,
      alignItemWithTrigger,
      alignOffset,
      anchor,
      collisionBoundary,
      collisionPadding,
      container,
      side,
      sideOffset,
      sticky,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = align;
    $[2] = alignItemWithTrigger;
    $[3] = alignOffset;
    $[4] = anchor;
    $[5] = children;
    $[6] = className;
    $[7] = collisionBoundary;
    $[8] = collisionPadding;
    $[9] = container;
    $[10] = props;
    $[11] = side;
    $[12] = sideOffset;
    $[13] = sticky;
    $[14] = t1;
  } else {
    align = $[1];
    alignItemWithTrigger = $[2];
    alignOffset = $[3];
    anchor = $[4];
    children = $[5];
    className = $[6];
    collisionBoundary = $[7];
    collisionPadding = $[8];
    container = $[9];
    props = $[10];
    side = $[11];
    sideOffset = $[12];
    sticky = $[13];
    t1 = $[14];
  }
  const position = t1 === undefined ? "popper" : t1;
  const t2 = alignItemWithTrigger ?? position !== "popper";
  const t3 = position === "popper" && "data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95";
  const t4 = position === "popper" && "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2";
  const t5 = position === "popper" && "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1";
  const t6 = position === "item-aligned" && "data-[side=none]:translate-y-px";
  let t7;
  if ($[15] !== className || $[16] !== t3 || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t7 = cn("relative z-50 min-w-16 overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md", t3, t4, t5, t6, "[--sq-r:.9375rem] sq:relative sq:rounded-(--sq-r) sq:shadow-none sq:drop-shadow-md", className);
    $[15] = className;
    $[16] = t3;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <SelectScrollUpButton />;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const t9 = position === "popper" && "h-(--anchor-height) w-full min-w-(--anchor-width) scroll-my-1";
  let t10;
  if ($[22] !== t9) {
    t10 = cn("p-1", t9);
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== children || $[25] !== t10) {
    t11 = <SelectPrimitive.List className={t10}>{children}</SelectPrimitive.List>;
    $[24] = children;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SelectScrollDownButton />;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== props || $[29] !== t11 || $[30] !== t7) {
    t13 = <SelectPrimitive.Popup data-slot="select-content" className={t7} {...props}>{t8}{t11}{t12}</SelectPrimitive.Popup>;
    $[28] = props;
    $[29] = t11;
    $[30] = t7;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== align || $[33] !== alignOffset || $[34] !== anchor || $[35] !== collisionBoundary || $[36] !== collisionPadding || $[37] !== side || $[38] !== sideOffset || $[39] !== sticky || $[40] !== t13 || $[41] !== t2) {
    t14 = <SelectPrimitive.Positioner align={align} alignItemWithTrigger={t2} alignOffset={alignOffset} anchor={anchor} collisionBoundary={collisionBoundary} collisionPadding={collisionPadding} side={side} sideOffset={sideOffset} sticky={sticky}>{t13}</SelectPrimitive.Positioner>;
    $[32] = align;
    $[33] = alignOffset;
    $[34] = anchor;
    $[35] = collisionBoundary;
    $[36] = collisionPadding;
    $[37] = side;
    $[38] = sideOffset;
    $[39] = sticky;
    $[40] = t13;
    $[41] = t2;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== container || $[44] !== t14) {
    t15 = <SelectPrimitive.Portal container={container}>{t14}</SelectPrimitive.Portal>;
    $[43] = container;
    $[44] = t14;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  return t15;
}
function SelectLabel(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("px-2 py-1.5 text-xs text-muted-foreground", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <SelectPrimitive.GroupLabel data-slot="select-label" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectItem(t0) {
  const $ = _c(14);
  let children;
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
  }
  let t1;
  if ($[4] !== className) {
    t1 = cn("relative flex h-6 w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-6 text-sm outline-hidden select-none data-disabled:pointer-events-none data-disabled:opacity-50 data-highlighted:bg-accent data-highlighted:text-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg:not([class*='text-'])]:text-muted-foreground *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2", "[--sq-r:.5rem] sq:rounded-(--sq-r)", className);
    $[4] = className;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="absolute left-0 flex size-3.5 w-6 items-center justify-center"><SelectPrimitive.ItemIndicator><CheckIcon className="size-4" /></SelectPrimitive.ItemIndicator></span>;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span />;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== children) {
    t4 = <SelectPrimitive.ItemText render={t3}>{children}</SelectPrimitive.ItemText>;
    $[8] = children;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== props || $[11] !== t1 || $[12] !== t4) {
    t5 = <SelectPrimitive.Item data-slot="select-item" className={t1} {...props}>{t2}{t4}</SelectPrimitive.Item>;
    $[10] = props;
    $[11] = t1;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}
function SelectSeparator(t0) {
  const $ = _c(8);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("pointer-events-none mx-3 my-1 h-px bg-border", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <div data-slot="select-separator" role="separator" className={t1} {...props} />;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SelectScrollUpButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("inset-x-0 top-0 z-10 flex h-6 cursor-default items-center justify-center bg-popover", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronUpIcon className="size-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollUpArrow data-slot="select-scroll-up-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollUpArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function SelectScrollDownButton(t0) {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("inset-x-0 bottom-0 z-10 flex h-6 cursor-default items-center justify-center bg-popover", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ChevronDownIcon className="size-4" />;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== props || $[7] !== t1) {
    t3 = <SelectPrimitive.ScrollDownArrow data-slot="select-scroll-down-button" className={t1} {...props}>{t2}</SelectPrimitive.ScrollDownArrow>;
    $[6] = props;
    $[7] = t1;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
export { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue };